import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import LeadFormModal from "./LeadFormModal";
import ContactFormModal from "./ContactFormModal";
import PhoneLink from "./PhoneLink";

export default function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About Us" },
    { href: "/your-path-forward", label: "Your Path Forward" },
    { href: "/projects", label: "Projects" },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans text-foreground bg-background">
      {/* Top Trust Bar - Forest Green */}
      <div className="bg-[oklch(0.25_0.1_145)] text-[oklch(0.97_0.01_85)] py-2 px-4 text-center text-sm font-medium tracking-wide">
        <span className="inline-block animate-pulse mr-2 text-[oklch(0.75_0.15_85)]">●</span>
        Direct Buyers • Local Renovators & Builders • Unconditional Offers • 500+ Projects
      </div>

      {/* Main Navigation */}
      <header
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300 border-b border-[oklch(0.9_0.02_85)]",
          isScrolled ? "bg-white/95 backdrop-blur-sm shadow-sm py-2" : "bg-white py-4"
        )}
      >
        <div className="container flex items-center justify-between">
          {/* Logo */}
          <Link href="/">
            <div className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="/logo-holowaty-sm.webp" 
                alt="Holowaty Homes" 
                className="h-10 w-auto object-contain"
                width="40"
                height="40"
              />
              <div className="flex flex-col">
                <span className="font-serif font-bold text-lg leading-none text-[oklch(0.25_0.1_145)]">
                  Holowaty Homes
                </span>
                <span className="text-xs text-[oklch(0.45_0.12_85)] font-semibold tracking-wider">
                  Direct Buyers • Renovators & Builders
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <span
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-[oklch(0.35_0.12_145)] cursor-pointer relative py-1 after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-0.5 after:bg-[oklch(0.75_0.15_85)] after:transition-all hover:after:w-full",
                    location === link.href ? "text-[oklch(0.25_0.1_145)] after:w-full" : "text-[oklch(0.4_0.03_145)]"
                  )}
                >
                  {link.label}
                </span>
              </Link>
            ))}
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center gap-4">
            <a
              href="tel:3062690254"
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).gtag_report_phone_conversion) {
                  (window as any).gtag_report_phone_conversion();
                }
              }}
              className="hidden md:flex items-center gap-2 text-[oklch(0.25_0.1_145)] font-bold hover:text-[oklch(0.35_0.12_145)] transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-[oklch(0.92_0.05_145)] flex items-center justify-center">
                <Phone className="w-4 h-4 text-[oklch(0.35_0.12_145)]" />
              </div>
              <span>(306) 269-0254</span>
            </a>
            <LeadFormModal 
              source="header"
              trigger={
                <Button className="hidden md:flex bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">
                  Let's Talk
                </Button>
              }
            />

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[oklch(0.4_0.03_145)] hover:bg-[oklch(0.95_0.01_85)] rounded-md"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-[oklch(0.9_0.02_85)] shadow-lg py-4 px-4 flex flex-col gap-4 animate-in slide-in-from-top-5">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <span
                  className={cn(
                    "block py-2 text-base font-medium border-b border-[oklch(0.95_0.01_85)] last:border-0",
                    location === link.href ? "text-[oklch(0.25_0.1_145)]" : "text-[oklch(0.4_0.03_145)]"
                  )}
                >
                  {link.label}
                </span>
              </Link>
            ))}
            <div className="pt-2 flex flex-col gap-3">
              <a
                href="tel:3062690254"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).gtag_report_phone_conversion) {
                    (window as any).gtag_report_phone_conversion();
                  }
                }}
                className="flex items-center justify-center gap-2 text-[oklch(0.25_0.1_145)] font-bold py-2 bg-[oklch(0.92_0.05_145)] rounded-md"
              >
                <Phone className="w-4 h-4" />
                (306) 269-0254
              </a>
              <LeadFormModal 
                source="mobile-menu"
                trigger={
                  <Button className="w-full bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold">
                    Let's Talk
                  </Button>
                }
              />
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer - Forest Green */}
      <footer className="bg-[oklch(0.2_0.08_145)] text-[oklch(0.85_0.03_85)] py-12 border-t-4 border-[oklch(0.75_0.15_85)]">
        <div className="container grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logo-holowaty-sm.webp" alt="Holowaty Homes" className="h-10 w-auto" width="40" height="40" />
              <div className="flex flex-col text-[oklch(0.97_0.01_85)]">
                <span className="font-serif font-bold text-lg">Holowaty Homes</span>
                <span className="text-xs text-[oklch(0.75_0.15_85)]">Direct Buyers • Renovators & Builders</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed">
              We are the actual buyers—local renovators and builders with our own funding. No middlemen, no contract flipping, no conditions. Just fair, unconditional offers.
            </p>
          </div>

          <div className="col-span-1">
            <h3 className="text-[oklch(0.97_0.01_85)] font-bold mb-4 uppercase tracking-wider text-sm">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link href="/"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Home</span></Link></li>
              <li><Link href="/about"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">About Us</span></Link></li>
              <li><Link href="/why-were-different"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Why We're Different</span></Link></li>
              <li><Link href="/creative-financing"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Creative Financing</span></Link></li>
              <li><Link href="/your-path-forward"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Your Path Forward</span></Link></li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="text-[oklch(0.97_0.01_85)] font-bold mb-4 uppercase tracking-wider text-sm">Resources</h3>
            <ul className="space-y-2 text-sm">
              <li><Link href="/list-your-home"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">List Your Home</span></Link></li>
              <li><Link href="/communities"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Communities</span></Link></li>
              <li><Link href="/projects"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">Projects</span></Link></li>
              <li><Link href="/wholesaling-truth"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">The Truth About "Cash Offers"</span></Link></li>
              <li><Link href="/faq"><span className="hover:text-[oklch(0.75_0.15_85)] transition-colors cursor-pointer">FAQ</span></Link></li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="text-[oklch(0.97_0.01_85)] font-bold mb-4 uppercase tracking-wider text-sm">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[oklch(0.75_0.15_85)] shrink-0" />
                <PhoneLink showIcon={false} className="font-medium hover:text-[oklch(0.75_0.15_85)] focus:outline-none focus:ring-2 focus:ring-[oklch(0.75_0.15_85)] focus:ring-offset-2 focus:ring-offset-[oklch(0.2_0.08_145)] rounded-sm">
                  (306) 269-0254
                </PhoneLink>
              </li>
              <li className="flex items-start gap-3">
                <ContactFormModal
                  trigger={
                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] rounded-md transition-colors text-sm font-medium cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center font-bold">✉</div>
                      Send Us a Message
                    </button>
                  }
                />
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 text-[oklch(0.75_0.15_85)] shrink-0 flex items-center justify-center">📍</div>
                <span>Calgary, Alberta<br/>Serving Inner City & Surrounding Areas</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="container pt-8 border-t border-[oklch(0.3_0.06_145)] text-xs text-[oklch(0.7_0.03_85)]">
          <p className="mb-4">
            <strong>Disclaimer:</strong> If your home does not qualify for an offer, we work with licensed real estate professionals who may be able to assist you with a traditional sale. Any referral to a licensed agent is made at no cost to you. Sell Your Home in Calgary is not a licensed real estate brokerage and does not provide real estate brokerage services. Licensed agents operate independently and are regulated by the Real Estate Council of Alberta (RECA).
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>&copy; {new Date().getFullYear()} Holowaty Homes. All rights reserved.</p>
            <p>Designed with integrity for Calgary homeowners.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
